import { UzBadge, UzButton } from '@utazzo/ui';
import { BedDouble, Minus, Plus, RotateCcw, X } from 'lucide-react';
import { useState } from 'react';

import { resolveStayLeg, roomsForStayFull, stayRoomsOverride } from '../stay-rooms';
import { useSetLegRoomsOverride } from '../use-itineraries';

import type { ItineraryDetail, LegRoomsOverride } from '@utazzo/shared';

/** One editable room in the override dialog (full room shape). */
interface EditRoom {
  adults: number;
  children: number;
  infants: number;
  childrenNoBed: number;
  childAges: number[];
}

const MAX_ROOMS = 8; // TBO caps a search at 8 rooms.
const MAX_ADULTS = 9;
const MAX_CHILDREN = 9;

const clamp = (n: number, min: number, max: number): number => Math.max(min, Math.min(max, n));

function summarize(rooms: { adults: number; children: number; infants: number }[]): string {
  const totals = rooms.reduce(
    (a, r) => ({
      adults: a.adults + r.adults,
      children: a.children + r.children,
      infants: a.infants + r.infants,
    }),
    { adults: 0, children: 0, infants: 0 },
  );
  const parts = [`${rooms.length} room${rooms.length === 1 ? '' : 's'}`];
  const guests = [
    `${totals.adults} adult${totals.adults === 1 ? '' : 's'}`,
    totals.children > 0 ? `${totals.children} child${totals.children === 1 ? '' : 'ren'}` : null,
    totals.infants > 0 ? `${totals.infants} infant${totals.infants === 1 ? '' : 's'}` : null,
  ].filter(Boolean);
  return `${parts[0]} · ${guests.join(', ')}`;
}

/**
 * Per-stay room-configuration control shown on a hotel slot. Displays the
 * stay's effective rooms (override or the itinerary's global config), flags a
 * custom override, and opens a small editor to set/clear the override. When set,
 * the stay's TBO hotel is searched/re-priced for that occupancy instead of the
 * global config.
 */
export function StayRoomsControl({
  detail,
  itineraryId,
  cityId,
  date,
  canEdit,
}: {
  detail: ItineraryDetail | null;
  itineraryId: string;
  cityId: string | null;
  /** The stay's day date, so a repeat-city stay resolves to its OWN leg. */
  date?: string | null;
  canEdit: boolean;
}): React.JSX.Element | null {
  const [open, setOpen] = useState(false);
  const leg = resolveStayLeg(detail, cityId, date);
  const override = stayRoomsOverride(detail, cityId, date);
  const hasOverride = override !== null;
  const effective = roomsForStayFull(detail, cityId, date);
  // Full editable rooms (incl. child ages) seeded from the override, else global.
  const editSource = override ?? (detail?.roomGroups ?? []).flatMap((g) => g.rooms);
  const initialRooms: EditRoom[] = editSource.map((r) => ({
    adults: Math.max(1, r.adults),
    children: r.children,
    infants: r.infants ?? 0,
    childrenNoBed: Math.min(r.childrenNoBed ?? 0, r.children),
    childAges: Array.from({ length: r.children }, (_, i) => r.childAges?.[i] ?? 8),
  }));

  // No leg to attach an override to (e.g. an unconfigured stay) -> nothing to do.
  if (!leg) return null;

  return (
    <div className="mb-1 flex items-center gap-2 text-xs text-zinc-500">
      <BedDouble className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="truncate">{summarize(effective)}</span>
      {hasOverride ? (
        <span className="shrink-0">
          <UzBadge tone="secondary">Custom rooms</UzBadge>
        </span>
      ) : null}
      {canEdit ? (
        <button
          type="button"
          className="shrink-0 font-medium text-indigo-600 hover:underline"
          onClick={() => setOpen(true)}
        >
          Configure rooms
        </button>
      ) : null}
      {open ? (
        <RoomOverrideDialog
          itineraryId={itineraryId}
          legId={leg.id}
          cityName={leg.cityName ?? 'this stay'}
          initialRooms={initialRooms}
          hasOverride={hasOverride}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
}

function RoomOverrideDialog({
  itineraryId,
  legId,
  cityName,
  initialRooms,
  hasOverride,
  onClose,
}: {
  itineraryId: string;
  legId: string;
  cityName: string;
  initialRooms: EditRoom[];
  hasOverride: boolean;
  onClose: () => void;
}): React.JSX.Element {
  const save = useSetLegRoomsOverride(itineraryId);
  const [rooms, setRooms] = useState<EditRoom[]>(
    initialRooms.length > 0
      ? initialRooms.map((r) => ({ ...r, childAges: [...r.childAges] }))
      : [{ adults: 2, children: 0, infants: 0, childrenNoBed: 0, childAges: [] }],
  );

  const patchRoom = (i: number, patch: Partial<EditRoom>): void =>
    setRooms((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const setChildren = (i: number, next: number): void =>
    setRooms((prev) =>
      prev.map((r, idx) => {
        if (idx !== i) return r;
        const children = clamp(next, 0, MAX_CHILDREN);
        const childAges = Array.from({ length: children }, (_, k) => r.childAges[k] ?? 8);
        return { ...r, children, childAges, childrenNoBed: Math.min(r.childrenNoBed, children) };
      }),
    );

  const addRoom = (): void =>
    setRooms((prev) =>
      prev.length >= MAX_ROOMS
        ? prev
        : [...prev, { adults: 2, children: 0, infants: 0, childrenNoBed: 0, childAges: [] }],
    );
  const removeRoom = (i: number): void =>
    setRooms((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i)));

  const onSave = (): void => {
    const payload: LegRoomsOverride = rooms.map((r) => ({
      adults: clamp(r.adults, 1, MAX_ADULTS),
      children: clamp(r.children, 0, MAX_CHILDREN),
      infants: clamp(r.infants, 0, MAX_CHILDREN),
      childrenNoBed: Math.min(r.childrenNoBed, r.children),
      childAges: r.childAges.slice(0, r.children).map((a) => clamp(a, 0, 17)),
    }));
    save.mutate({ legId, rooms: payload }, { onSuccess: onClose });
  };

  const onReset = (): void => save.mutate({ legId, rooms: null }, { onSuccess: onClose });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-label="Configure stay rooms"
        className="max-h-[85vh] w-full max-w-lg overflow-auto rounded-xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-start justify-between">
          <div>
            <h3 className="text-base font-semibold text-zinc-900">Rooms for {cityName}</h3>
            <p className="mt-0.5 text-xs text-zinc-500">
              Override the room split for this stay only. The TBO hotel search and re-price use
              this; other stays keep the itinerary&apos;s global rooms.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-zinc-400">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-3 space-y-3">
          {rooms.map((room, i) => (
            <div key={i} className="rounded-lg border border-zinc-200 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-zinc-700">Room {i + 1}</span>
                {rooms.length > 1 ? (
                  <button
                    type="button"
                    className="text-xs text-rose-600 hover:underline"
                    onClick={() => removeRoom(i)}
                  >
                    Remove
                  </button>
                ) : null}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Stepper
                  label="Adults"
                  value={room.adults}
                  min={1}
                  max={MAX_ADULTS}
                  onChange={(v) => patchRoom(i, { adults: v })}
                />
                <Stepper
                  label="Children"
                  value={room.children}
                  min={0}
                  max={MAX_CHILDREN}
                  onChange={(v) => setChildren(i, v)}
                />
                <Stepper
                  label="Infants"
                  value={room.infants}
                  min={0}
                  max={MAX_CHILDREN}
                  onChange={(v) => patchRoom(i, { infants: v })}
                />
              </div>
              {room.children > 0 ? (
                <div className="mt-2">
                  <span className="text-xs text-zinc-500">Child ages</span>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {room.childAges.map((age, k) => (
                      <input
                        key={k}
                        type="number"
                        min={0}
                        max={17}
                        value={age}
                        onChange={(e) =>
                          patchRoom(i, {
                            childAges: room.childAges.map((a, idx) =>
                              idx === k ? clamp(Number(e.target.value) || 0, 0, 17) : a,
                            ),
                          })
                        }
                        className="w-14 rounded-md border border-zinc-300 px-2 py-1 text-sm"
                        aria-label={`Room ${i + 1} child ${k + 1} age`}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ))}
          {rooms.length < MAX_ROOMS ? (
            <UzButton variant="outline" size="sm" onClick={addRoom}>
              <Plus className="mr-1 h-4 w-4" /> Add room
            </UzButton>
          ) : null}
        </div>

        <div className="mt-5 flex items-center justify-between">
          <div>
            {hasOverride ? (
              <UzButton variant="ghost" size="sm" onClick={onReset} disabled={save.isPending}>
                <RotateCcw className="mr-1 h-4 w-4" /> Reset to global
              </UzButton>
            ) : null}
          </div>
          <div className="flex gap-2">
            <UzButton variant="outline" size="sm" onClick={onClose} disabled={save.isPending}>
              Cancel
            </UzButton>
            <UzButton size="sm" onClick={onSave} disabled={save.isPending}>
              Save rooms
            </UzButton>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stepper({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}): React.JSX.Element {
  return (
    <div>
      <span className="text-xs text-zinc-500">{label}</span>
      <div className="mt-1 flex items-center gap-1">
        <button
          type="button"
          className="flex h-7 w-7 items-center justify-center rounded-md border border-zinc-300 disabled:opacity-40"
          onClick={() => onChange(clamp(value - 1, min, max))}
          disabled={value <= min}
          aria-label={`Decrease ${label}`}
        >
          <Minus className="h-3.5 w-3.5" />
        </button>
        <span className="w-6 text-center text-sm font-medium">{value}</span>
        <button
          type="button"
          className="flex h-7 w-7 items-center justify-center rounded-md border border-zinc-300 disabled:opacity-40"
          onClick={() => onChange(clamp(value + 1, min, max))}
          disabled={value >= max}
          aria-label={`Increase ${label}`}
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
